// controllers/userController.js  (frontend)
// Same pattern as subscriptionController.js — uses apiService, authController,
// role guards, and returns { success, data, message } objects.

import apiService from '../services/apiService';
import { authController }                      from './authController';
import { User,}                   from '../models/User';

// ─── Internal helpers ─────────────────────────────────────────────────────────
const _getUser = () => authController.getCurrentUser();
const _getRole = () => _getUser()?.role || null;
const _getUid  = () => { const u = _getUser(); return u?.id || u?._id || null; };
// const _getCid  = () => { const u = _getUser(); return u?.companyId?._id || u?.companyId || null; };

/**
 * Role guard — same pattern as subscriptionController._guard()
 */
const _guard = (allowedRoles) => {
  const role = _getRole();
  if (!role)                        return { blocked: true, reason: 'Not authenticated. Please log in.' };
  if (!allowedRoles.includes(role)) return { blocked: true, reason: `Access denied. Requires: ${allowedRoles.join(' or ')}` };
  return { blocked: false };
};

/** Management roles that can access user CRUD */
const MGMT = ['super_admin', 'admin', 'super_distributor', 'distributor', 'subdistributor', 'retailer'];
const SADM = ['super_admin', 'admin'];
const SA   = ['super_admin'];

// ── Normalise API users → User class instances ────────────────────────────────
const toUser  = (raw)  => new User(raw);
const toUsers = (arr)  => (Array.isArray(arr) ? arr : []).map(toUser);

// ── Query string builder (same util as subscriptionController) ─────────────────
function qs(params = {}) {
  const str = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'All')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return str ? `?${str}` : '';
}

// ─────────────────────────────────────────────────────────────────────────────
export const userController = {

  // ── Auth helpers (same pattern as subscriptionController) ──────────────────
  getCurrentUser: () => _getUser(),
  getCurrentRole: () => _getRole(),
  isSuperAdmin:   () => _getRole() === 'super_admin',
  isAdmin:        () => SADM.includes(_getRole()),

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTH
  // ═══════════════════════════════════════════════════════════════════════════


  /** POST /users/forgot-password */
  forgotPassword: async (email) => {
    try {
      const res = await apiService.post('users/forgot-password', { email });
      return { success: true, message: res.message };
    } catch (e) { return { success: false, message: e.message }; }
  },

  /** POST /users/reset-password/:token */
  resetPassword: async (token, password) => {
    try {
      const res = await apiService.post(`users/reset-password/${token}`, { password });
      return { success: true, message: res.message };
    } catch (e) { return { success: false, message: e.message, errorCode: e.errorCode }; }
  },

  /** GET /users/verify-email/:token */
  verifyEmail: async (token) => {
    try {
      const res = await apiService.get(`users/verify-email/${token}`);
      return { success: true, message: res.message };
    } catch (e) { return { success: false, message: e.message }; }
  },

  /** POST /users/resend-verification */
  resendVerification: async (email) => {
    try {
      const res = await apiService.post('users/resend-verification', { email });
      return { success: true, message: res.message };
    } catch (e) { return { success: false, message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // OWN PROFILE
  // ═══════════════════════════════════════════════════════════════════════════

  /** GET /users/me */
  getMe: async () => {
    try {
      const res = await apiService.get('users/me');
      return { success: true, data: toUser(res.user) };
    } catch (e) { return { success: false, message: e.message }; }
  },

  /** PUT /users/profile — supports avatar (FormData) or JSON */
updateProfile: async (data) => {
  try {
    const isFile = data instanceof FormData;
    const res = isFile
      ? await apiService.putFormData('users/profile', data)   // ✅ FIXED (was: apiService.putUpload)
      : await apiService.put('users/profile', data);
    return { success: true, data: toUser(res.user), message: res.message };
  } catch (e) {
    return { success: false, message: e.message, errors: e.errors };
  }
},

  /** PUT /users/change-password */
  changePassword: async (currentPassword, newPassword) => {
    try {
      const res = await apiService.put('users/change-password', { currentPassword, newPassword });
      return { success: true, message: res.message };
    } catch (e) { return { success: false, message: e.message, errorCode: e.errorCode }; }
  },

  /** DELETE /users/account */
//   deleteAccount: async (password) => {
//     try {
//       const res = await apiService.delete('users/account', { password });
//       clearTokens();
//       return { success: true, message: res.message };
//     } catch (e) { return { success: false, message: e.message }; }
//   },

  // ═══════════════════════════════════════════════════════════════════════════
  // USER MANAGEMENT  (all scoped to caller's downline by backend)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * GET /users  — paginated, filtered list (scoped to downline on backend)
   *
   * filters: { role, status, search, companyId }
   * sort:    { field, dir }
   * Returns: { success, data: User[], total, totalPages, page }
   */
  getAll: async ({ filters = {}, sort = {}, page = 1, limit = 20 } = {}) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const params = { page, limit, ...filters };
      if (sort.field) { params.sortBy = sort.field; params.sortOrder = sort.dir || 'desc'; }
      const res = await apiService.get(`users${qs(params)}`);
      return {
        success: true,
        data:       toUsers(res.users),
        total:      res.count      || 0,
        totalPages: res.totalPages || 1,
        page:       res.currentPage|| page,
      };
    } catch (e) { return { success: false, data: [], message: e.message }; }
  },

  /**
   * Convenience wrapper — getAll with role pre-filled.
   * Used by AdminsPage, DistributorsPage, etc.
   */
  getByRole: async (role, opts = {}) => {
    return userController.getAll({ ...opts, filters: { ...opts.filters, role } });
  },

  /** GET /users/:id */
  getById: async (id) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`users/${id}`);
      return { success: true, data: toUser(res.user) };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },

  /**
   * POST /users/create-member
   * Creates a user under the logged-in user.
   * Supports FormData (avatar) or plain object.
   *
   * Backend enforces:
   *  - role hierarchy (can only create roles below yours)
   *  - same companyId as caller (unless super_admin)
   *  - parentId defaults to caller
   */
create: async (data) => {
  const g = _guard(MGMT);
  if (g.blocked) return { success: false, message: g.reason };
  try {
    const isFile = data instanceof FormData;
    const res = isFile
      ? await apiService.postFormData('users/create-member', data)   // ✅ FIXED (was: apiService.upload)
      : await apiService.post('users/create-member', data);
    return { success: true, data: toUser(res.user), message: res.message || 'User created successfully' };
  } catch (e) {
    return { success: false, message: e.message, errors: e.errors, errorCode: e.errorCode };
  }
},

  /** PUT /users/:id */
  update: async (id, data) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`users/${id}`, data);
      return { success: true, data: toUser(res.user), message: res.message || 'User updated successfully' };
    } catch (e) {
      return { success: false, message: e.message, errors: e.errors };
    }
  },

  /** DELETE /users/:id  (soft-delete on backend) */
  delete: async (id) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.delete(`users/${id}`);
      return { success: true, message: res.message || 'User deleted successfully' };
    } catch (e) { return { success: false, message: e.message }; }
  },

  /**
   * Bulk delete — sequential calls (backend has no bulk-delete route).
   * Returns aggregate result.
   */
  bulkDelete: async (ids = []) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, message: g.reason };
    let deleted = 0, failed = 0;
    await Promise.allSettled(
      ids.map(id =>
        apiService.delete(`users/${id}`)
          .then(() => deleted++)
          .catch(() => failed++)
      )
    );
    return {
      success: failed === 0,
      message: `Deleted ${deleted}${failed ? `, ${failed} failed` : ''} user(s)`,
      deleted, failed,
    };
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // STATUS OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /** Toggle isBlocked — PUT /users/:id  with { isBlocked, blockReason } */
  toggleBlock: async (id, reason = '') => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      // First fetch current state
      const current = await apiService.get(`users/${id}`);
      const nowBlocked = !current.user?.isBlocked;
      const res = await apiService.put(`users/${id}`, {
        isBlocked: nowBlocked,
        blockReason: nowBlocked ? (reason || 'Blocked by admin') : '',
      });
      return {
        success: true,
        data: toUser(res.user),
        message: nowBlocked ? 'User blocked successfully' : 'User unblocked successfully',
      };
    } catch (e) { return { success: false, message: e.message }; }
  },

  /** Toggle isActive — PUT /users/:id */
  toggleActive: async (id) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const current = await apiService.get(`users/${id}`);
      const nowActive = !current.user?.isActive;
      const res = await apiService.put(`users/${id}`, { isActive: nowActive });
      return {
        success: true,
        data: toUser(res.user),
        message: nowActive ? 'User activated' : 'User deactivated',
      };
    } catch (e) { return { success: false, message: e.message }; }
  },

  /** Verify user — PUT /users/:id with { isVerified: true } */
  verify: async (id) => {
    const g = _guard(SADM);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`users/${id}`, { isVerified: true });
      return { success: true, data: toUser(res.user), message: 'User verified successfully' };
    } catch (e) { return { success: false, message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HIERARCHY
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * GET /users/:id/hierarchy-tree
   * Returns nested tree: { ...user, children: [ ...children ] }
   */
  getHierarchy: async (rootId = null) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const id  = rootId || _getUid();
      const res = await apiService.get(`users/${id}/hierarchy-tree`);
      return { success: true, data: res.tree };
    } catch (e) { return { success: false, data: [], message: e.message }; }
  },

  /**
   * GET /users/:id/downline
   * Flat list of all descendants
   */
  getDownline: async (userId) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const id  = userId || _getUid();
      const res = await apiService.get(`users/${id}/downline`);
      return { success: true, data: toUsers(res.users), total: res.count };
    } catch (e) { return { success: false, data: [], message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PERMISSIONS & COMMISSION
  // ═══════════════════════════════════════════════════════════════════════════

  /** PUT /users/:id — update permissions array */
  updatePermissions: async (id, permissions = []) => {
    const g = _guard(SADM);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`users/${id}`, { permissions });
      return { success: true, data: toUser(res.user), message: 'Permissions updated successfully' };
    } catch (e) { return { success: false, message: e.message }; }
  },

  /** PUT /users/:id — update commissionRate */
  updateCommission: async (id, commissionRate) => {
    const g = _guard(SADM);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`users/${id}`, { commissionRate: Number(commissionRate) });
      return { success: true, data: toUser(res.user), message: 'Commission updated successfully' };
    } catch (e) { return { success: false, message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // STATS
  // ═══════════════════════════════════════════════════════════════════════════

  /** GET /users/stats — scoped to caller's downline on backend */
  getStats: async () => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get('users/stats');
      return { success: true, data: res.stats };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIVITY LOGS  (mock fallback — replace when backend adds /users/logs)
  // ═══════════════════════════════════════════════════════════════════════════

  getActivityLogs: async ({ userId, page = 1, limit = 20 } = {}) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      // Use real endpoint when available:
      // const res = await apiService.get(`users/activity-logs${qs({ userId, page, limit })}`);
      // return { success: true, data: res.logs, total: res.total, totalPages: res.totalPages };

      // ── Temporary mock (remove when backend is ready) ──
      const actions = ['Login','Logout','Profile Updated','Password Changed','Wallet Credited','Order Placed','Permission Changed','Status Changed'];
      const logs = Array.from({ length: 50 }, (_, i) => ({
        id: `log_${i}`,
        userId:   userId || 'unknown',
        userName: 'System',
        action:   actions[i % actions.length],
        ip:       `192.168.1.${(i % 254) + 1}`,
        device:   ['Android','iOS','Web','Desktop'][i % 4],
        createdAt: new Date(Date.now() - i * 3600000).toISOString(),
      }));
      const start = (page - 1) * limit;
      return {
        success: true,
        data:       logs.slice(start, start + limit),
        total:      logs.length,
        totalPages: Math.ceil(logs.length / limit),
      };
    } catch (e) { return { success: false, data: [], message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPANY MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /** GET /users/companies */
  getCompanies: async () => {
    const g = _guard(SADM);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const res = await apiService.get('users/companies');
      return { success: true, data: res.companies || [], total: res.count };
    } catch (e) { return { success: false, data: [], message: e.message }; }
  },

  /** GET /users/companies/:id */
  getCompanyById: async (id) => {
    const g = _guard(MGMT);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`users/companies/${id}`);
      return { success: true, data: res.company };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },

  /** POST /users/companies  (super_admin only) */
  createCompany: async (data) => {
    const g = _guard(SA);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('users/companies', data);
      return { success: true, data: res.company, message: res.message || 'Company created successfully' };
    } catch (e) {
      return { success: false, message: e.message, errors: e.errors };
    }
  },

  /** PUT /users/companies/:id */
  updateCompany: async (id, data) => {
    const g = _guard(SADM);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`users/companies/${id}`, data);
      return { success: true, data: res.company, message: res.message || 'Company updated successfully' };
    } catch (e) { return { success: false, message: e.message }; }
  },

  /** GET /users/companies/default — get or auto-create default company */
  getOrCreateDefaultCompany: async () => {
    const g = _guard(SA);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get('users/companies/default');
      return { success: true, data: res.company };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // WALLET
  // ═══════════════════════════════════════════════════════════════════════════

  /** GET /users/wallet */
  getWallet: async () => {
    try {
      const res = await apiService.get('users/wallet');
      return { success: true, data: res.wallet };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },

  /** POST /users/wallet/credit */
  addMoney: async ({ amount, description, paymentMethod, referenceId } = {}) => {
    try {
      const res = await apiService.post('users/wallet/credit', { amount, description, paymentMethod, referenceId });
      return { success: true, data: res.transaction, newBalance: res.newBalance, message: res.message };
    } catch (e) { return { success: false, message: e.message, errorCode: e.errorCode }; }
  },

  /** POST /users/wallet/debit (KYC required on backend) */
  withdrawMoney: async ({ amount, description, bankAccountId } = {}) => {
    try {
      const res = await apiService.post('users/wallet/debit', { amount, description, bankAccountId });
      return { success: true, data: res.transaction, newBalance: res.newBalance, message: res.message };
    } catch (e) { return { success: false, message: e.message, errorCode: e.errorCode }; }
  },

  /** GET /users/wallet/transactions */
  getTransactions: async ({ limit = 20, skip = 0, type } = {}) => {
    try {
      const res = await apiService.get(`users/wallet/transactions${qs({ limit, skip, type })}`);
      return { success: true, data: res.transactions, total: res.count };
    } catch (e) { return { success: false, data: [], message: e.message }; }
  },

  /** GET /users/wallet/bank-accounts */
  getBankAccounts: async () => {
    try {
      const res = await apiService.get('users/wallet/bank-accounts');
      return { success: true, data: res.bankAccounts, total: res.count };
    } catch (e) { return { success: false, data: [], message: e.message }; }
  },

  /** POST /users/wallet/bank-account */
  addBankAccount: async (data) => {
    try {
      const res = await apiService.post('users/wallet/bank-account', data);
      return { success: true, message: res.message };
    } catch (e) { return { success: false, message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FCM TOKENS
  // ═══════════════════════════════════════════════════════════════════════════

  addFCMToken: async ({ fcmToken, deviceType, deviceId, deviceName } = {}) => {
    try {
      const res = await apiService.post('users/fcm-token', { fcmToken, deviceType, deviceId, deviceName });
      return { success: true, message: res.message };
    } catch (e) { return { success: false, message: e.message }; }
  },

  removeFCMToken: async (fcmToken) => {
    try {
      const res = await apiService.delete('users/fcm-token', { fcmToken });
      return { success: true, message: res.message };
    } catch (e) { return { success: false, message: e.message }; }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CSV EXPORT  (client-side, no API call needed)
  // ═══════════════════════════════════════════════════════════════════════════

  exportCSV: (users = []) => {
    const headers = ['ID','Name','Email','Phone','Role','Status','Commission','Wallet','Parent','Company','Joined'];
    const rows = users.map(u => [
      u.customId,
      u.fullName,
      u.email,
      u.phone,
      u.displayRole,
      u.getStatusLabel(),
      `${u.commissionRate}%`,
      `₹${u.walletBalance.toLocaleString('en-IN')}`,
      u.parentName,
      u.companyName,
      new Date(u.joinDate).toLocaleDateString('en-IN'),
    ]);
    const csv  = [headers, ...rows].map(r => r.map(c => `"${String(c ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a    = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `users_${Date.now()}.csv` });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  },
};

export default userController;